/**
 * useCacheSync Hook
 *
 * React hook for cache sync state monitoring.
 *
 * NOTE: SSE connection is now handled internally by cacheStore.refreshCache().
 * This hook exists for backward compatibility but no longer creates SSE connections.
 * The cacheStore handles dev mode auth, Vite proxy routing, and progress updates directly.
 *
 * @deprecated SSE is now handled by cacheStore.refreshCache() internally.
 */

import { useEffect } from 'react';
import { useCacheStore } from '../store/cacheStore';

/**
 * Hook for monitoring cache sync state
 *
 * Previously this hook created an SSE connection when refresh started.
 * Now cacheStore.refreshCache() handles SSE internally with proper:
 * - Dev mode API key authentication
 * - Vite proxy routing (relative URLs)
 * - Progress updates via EventSource
 *
 * This hook is kept for backward compatibility but is now a no-op.
 *
 * @example
 * ```tsx
 * function App() {
 *   useCacheSync(); // Now a no-op, safe to call
 *
 *   return <div>...</div>;
 * }
 * ```
 */
export function useCacheSync() {
  const { isRefreshing } = useCacheStore();

  /**
   * Log when refresh state changes (for debugging)
   */
  useEffect(() => {
    if (isRefreshing) {
      console.log('[useCacheSync] Cache refresh in progress (SSE handled by cacheStore)');
    }
  }, [isRefreshing]);

  // No-op - SSE is handled by cacheStore.refreshCache() directly
  const connectSSE = () => {
    console.log('[useCacheSync] connectSSE is deprecated - SSE handled by cacheStore');
  };

  return { connectSSE };
}
